/**
 * Login Page
 * Provides email magic link and OAuth sign-in options
 */

import { type FormEvent, useState, Suspense } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@platform/ui";
import { Input } from "@platform/ui";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@platform/ui";

function LoginForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const { signInWithEmail, signInWithOAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectedFrom = searchParams.get("redirectedFrom");
  const urlError = searchParams.get("error");

  const handleMagicLinkSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const result = await signInWithEmail(email);
      if (result.success) {
        setMessage(result.message);
      } else {
        setError(result.message);
      }
    } catch (_err) {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: "google" | "github") => {
    setLoading(true);
    setError("");

    try {
      const { url } = await signInWithOAuth(provider);
      window.location.href = url;
    } catch (_err) {
      setError(`Failed to sign in with ${provider}. Please try again.`);
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-center mb-4">
          <div className="h-12 w-12 rounded-md bg-[var(--color-primary-500)] flex items-center justify-center">
            <span className="text-white font-bold text-2xl">F</span>
          </div>
        </div>
        <CardTitle className="text-center">Sign in to FindOp</CardTitle>
        {redirectedFrom && (
          <CardDescription className="text-center">
            You need to sign in to access this page
          </CardDescription>
        )}
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {urlError && (
            <div
              className="rounded-md bg-[var(--color-error-50)] border border-[var(--color-error-500)] p-4"
              role="alert"
            >
              <p className="text-sm text-[var(--color-error-500)]">
                {urlError}
              </p>
            </div>
          )}

          {message && (
            <div
              className="rounded-md bg-[var(--color-success-50)] border border-[var(--color-success-500)] p-4"
              role="status"
            >
              <p className="text-sm text-[var(--color-text-primary)]">
                {message}
              </p>
            </div>
          )}

          {error && (
            <div
              className="rounded-md bg-[var(--color-error-50)] border border-[var(--color-error-500)] p-4"
              role="alert"
            >
              <p className="text-sm text-[var(--color-error-500)]">{error}</p>
            </div>
          )}

          <form onSubmit={handleMagicLinkSignIn} className="space-y-4">
            <Input
              id="email"
              label="Email address"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
              autoComplete="email"
              aria-label="Email address"
            />
            <Button
              type="submit"
              disabled={loading}
              className="w-full"
              aria-busy={loading}
            >
              {loading ? "Sending..." : "Send magic link"}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--color-border-default)]" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]">
                Or continue with
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOAuthSignIn("google")}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2"
              aria-label="Sign in with Google"
            >
              <svg
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
              </svg>
              <span>Google</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOAuthSignIn("github")}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2"
              aria-label="Sign in with GitHub"
            >
              <svg
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                  clipRule="evenodd"
                />
              </svg>
              <span>GitHub</span>
            </Button>
          </div>

          <div className="space-y-2 text-center text-sm">
            <p className="text-[var(--color-text-secondary)]">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="text-[var(--color-primary-500)] hover:underline font-medium"
              >
                Create one
              </Link>
            </p>
            <p className="text-[var(--color-text-secondary)]">
              <Link
                to="/forgot-password"
                className="text-[var(--color-primary-500)] hover:underline"
              >
                Forgot your password?
              </Link>
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center space-y-4">
        <div
          className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[var(--color-primary-500)] border-r-transparent"
          role="status"
        />
        <p className="text-sm text-[var(--color-text-secondary)]">Loading...</p>
      </div>
    </div>
  );
}

export function LoginPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <LoginForm />
    </Suspense>
  );
}
